import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import {
  SchoolData,
  DistrictAgg,
  StatsResult,
  FilterState,
  ChoroplethIndicator,
  CoastalStats,
  CoastalResult,
  NearestNeighborResult,
  DistanceBin,
  ISLAND_REGIONS,
  COASTAL_THRESHOLD_KM,
  getSchoolLevel,
  getIsland,
  normalizeType,
} from './types';
import {
  extractCoastlinePolygon,
  createCoastalZonePolygon,
  computeNearestNeighbors,
  computeCoastalDistances,
} from './geoUtils';
import { SelectChangeEvent } from '@mui/material';

/**
 * Custom hook that encapsulates all data fetching, filtering, and aggregation
 * logic for the SchoolMapReport page.
 */
export function useSchoolData() {
  // ── Filter state ──────────────────────────────────────────────────────
  const [islandFilter, setIslandFilter] = useState<string>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [districtFilter, setDistrictFilter] = useState<string>('');
  const [typeFilters, setTypeFilters] = useState<Record<string, boolean>>({
    PUBLIC: true,
    PRIVATE: true,
  });
  const [levelFilters, setLevelFilters] = useState<Record<string, boolean>>({
    'Pre-Primary': true,
    Primary: true,
    Secondary: true,
    Basic: true,
  });
  const [choroplethIndicator, setChoroplethIndicator] = useState<ChoroplethIndicator>('enrollment');

  // ── Data fetching ─────────────────────────────────────────────────────

  // Fetch all schools from the EMIS Toolbox backend
  const {
    data: schoolsResponse,
    isLoading: isLoadingSchools,
    isError: isErrorSchools,
    error: errorSchools,
    refetch: refetchSchools,
  } = useQuery({
    queryKey: ['schoolsList'],
    queryFn: () => api.get('/sis-integration/schools/'),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch GeoJSON for district boundaries
  const { data: districtsGeoJSON } = useQuery({
    queryKey: ['districtsGeoJSON'],
    queryFn: async () => {
      const response = await fetch('/geojson/zanzibar_districts.geojson');
      return response.json();
    },
    staleTime: Infinity,
  });

  // Fetch GeoJSON for region boundaries
  const { data: regionsGeoJSON } = useQuery({
    queryKey: ['regionsGeoJSON'],
    queryFn: async () => {
      const response = await fetch('/geojson/zanzibar_regions.geojson');
      return response.json();
    },
    staleTime: Infinity,
  });

  // Fetch GeoJSON for ward boundaries
  const { data: wardsGeoJSON } = useQuery({
    queryKey: ['wardsGeoJSON'],
    queryFn: async () => {
      const response = await fetch('/geojson/zanzibar_wards.geojson');
      return response.json();
    },
    staleTime: Infinity,
  });

  // Fetch regions list for filter dropdown
  const { data: regionsData } = useQuery({
    queryKey: ['regionsList'],
    queryFn: () => api.get('/sis-integration/regions/'),
  });

  // ── Derived / computed data ───────────────────────────────────────────

  // All schools from the API response
  const allSchools: SchoolData[] = useMemo(() => {
    return schoolsResponse?.data?.data || [];
  }, [schoolsResponse]);

  // All regions from the API response
  const allRegions = useMemo(() => {
    return regionsData?.data?.data || [];
  }, [regionsData]);

  // Regions filtered by selected island
  const regions = useMemo(() => {
    if (!islandFilter) return allRegions;
    const islandRegionNames = ISLAND_REGIONS[islandFilter] || [];
    return allRegions.filter((r: any) => islandRegionNames.includes(r.region_name));
  }, [allRegions, islandFilter]);

  // Unique districts derived from schools, filtered by island + region
  const districts = useMemo(() => {
    const districtSet = new Map<string, string>();
    allSchools.forEach((s) => {
      if (!s.district) return;
      if (islandFilter && getIsland(s) !== islandFilter) return;
      if (regionFilter && s.region?.region_name !== regionFilter) return;
      districtSet.set(s.district.district_code, s.district.district_name);
    });
    return Array.from(districtSet.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allSchools, islandFilter, regionFilter]);

  // Apply all active filters to produce the visible school list
  const filteredSchools = useMemo(() => {
    return allSchools.filter((school) => {
      if (islandFilter && getIsland(school) !== islandFilter) return false;
      if (regionFilter && school.region?.region_name !== regionFilter) return false;
      if (districtFilter && school.district?.district_name !== districtFilter) return false;
      const schoolTypeKey = normalizeType(school.school_type);
      if (!typeFilters[schoolTypeKey]) return false;
      const level = getSchoolLevel(school);
      if (!levelFilters[level]) return false;
      return true;
    });
  }, [allSchools, islandFilter, regionFilter, districtFilter, typeFilters, levelFilters]);

  // Schools that have valid GPS coordinates
  const gpsSchools = useMemo(() => {
    return filteredSchools.filter((s) => s.latitude !== null && s.longitude !== null);
  }, [filteredSchools]);

  // Aggregate data by district for choropleth rendering and summary tables
  const districtAggregates = useMemo(() => {
    const agg = new Map<string, DistrictAgg>();
    filteredSchools.forEach((s) => {
      if (!s.district) return;
      const key = s.district.district_name.toLowerCase().trim();
      const existing = agg.get(key) || {
        district_name: s.district.district_name,
        district_code: s.district.district_code,
        region_name: s.region?.region_name || '',
        total_schools: 0,
        total_students: 0,
        male_students: 0,
        female_students: 0,
        total_teachers: 0,
        ptr: 0,
        gpi: null as number | null,
      };
      existing.total_schools += 1;
      existing.total_students += s.current_enrollment;
      existing.male_students += s.male_students;
      existing.female_students += s.female_students;
      existing.total_teachers += s.total_teachers;
      agg.set(key, existing);
    });

    // Compute PTR and GPI for each district after totals are accumulated
    agg.forEach((d) => {
      d.ptr = d.total_teachers > 0 ? Math.round(d.total_students / d.total_teachers) : 0;
      d.gpi = d.male_students > 0
        ? parseFloat((d.female_students / d.male_students).toFixed(2))
        : null;
    });

    return agg;
  }, [filteredSchools]);

  // Overall statistics across all filtered schools
  const stats: StatsResult = useMemo(() => {
    const govCount = filteredSchools.filter((s) => normalizeType(s.school_type) === 'PUBLIC').length;
    const privCount = filteredSchools.filter((s) => normalizeType(s.school_type) === 'PRIVATE').length;
    const levelCounts: Record<string, number> = {};
    let totalStudents = 0;
    let totalTeachers = 0;
    let maleStudents = 0;
    let femaleStudents = 0;

    filteredSchools.forEach((s) => {
      const level = getSchoolLevel(s);
      levelCounts[level] = (levelCounts[level] || 0) + 1;
      totalStudents += s.current_enrollment;
      totalTeachers += s.total_teachers;
      maleStudents += s.male_students;
      femaleStudents += s.female_students;
    });

    return {
      total: filteredSchools.length,
      government: govCount,
      private: privCount,
      levels: levelCounts,
      totalStudents,
      totalTeachers,
      maleStudents,
      femaleStudents,
      ptr: totalTeachers > 0 ? Math.round(totalStudents / totalTeachers) : 0,
      gpi: maleStudents > 0
        ? parseFloat((femaleStudents / maleStudents).toFixed(2))
        : null,
      withGPS: gpsSchools.length,
    };
  }, [filteredSchools, gpsSchools]);

  // ── Proximity & coastal analysis ─────────────────────────────────────

  // Merged coastline polygon (computed once from regionsGeoJSON)
  const coastlinePolygon = useMemo(() => {
    if (!regionsGeoJSON) return null;
    return extractCoastlinePolygon(regionsGeoJSON);
  }, [regionsGeoJSON]);

  // 5 km coastal zone polygon for map overlay
  const coastalZoneGeoJSON = useMemo(() => {
    if (!coastlinePolygon) return null;
    return createCoastalZonePolygon(coastlinePolygon, COASTAL_THRESHOLD_KM);
  }, [coastlinePolygon]);

  // Nearest neighbor for each GPS school
  const nearestNeighborMap = useMemo(() => {
    return computeNearestNeighbors(gpsSchools);
  }, [gpsSchools]);

  // Coastal distance for each GPS school
  const coastalDistanceMap = useMemo(() => {
    if (!coastlinePolygon) return new Map<string, CoastalResult>();
    return computeCoastalDistances(gpsSchools, coastlinePolygon, COASTAL_THRESHOLD_KM);
  }, [gpsSchools, coastlinePolygon]);

  // Coastal zone aggregate stats
  const coastalStats: CoastalStats = useMemo(() => {
    const coastalSchools = gpsSchools.filter(
      (s) => coastalDistanceMap.get(s.school_id)?.isCoastalZone
    );
    const totalStudents = coastalSchools.reduce((sum, s) => sum + s.current_enrollment, 0);
    const maleStudents = coastalSchools.reduce((sum, s) => sum + s.male_students, 0);
    const femaleStudents = coastalSchools.reduce((sum, s) => sum + s.female_students, 0);
    const totalTeachers = coastalSchools.reduce((sum, s) => sum + s.total_teachers, 0);

    return {
      schoolCount: coastalSchools.length,
      totalStudents,
      maleStudents,
      femaleStudents,
      totalTeachers,
      ptr: totalTeachers > 0 ? Math.round(totalStudents / totalTeachers) : 0,
      percentOfAll: gpsSchools.length > 0
        ? Math.round((coastalSchools.length / gpsSchools.length) * 100)
        : 0,
    };
  }, [gpsSchools, coastalDistanceMap]);

  // Distance distribution histogram bins
  const distanceDistribution: DistanceBin[] = useMemo(() => {
    const bins: DistanceBin[] = [
      { range: '0-0.5', label: '<500m', count: 0 },
      { range: '0.5-1', label: '0.5–1km', count: 0 },
      { range: '1-2', label: '1–2km', count: 0 },
      { range: '2-5', label: '2–5km', count: 0 },
      { range: '5-10', label: '5–10km', count: 0 },
      { range: '10+', label: '>10km', count: 0 },
    ];
    nearestNeighborMap.forEach((nn) => {
      const d = nn.distanceKm;
      if (d < 0.5) bins[0].count++;
      else if (d < 1) bins[1].count++;
      else if (d < 2) bins[2].count++;
      else if (d < 5) bins[3].count++;
      else if (d < 10) bins[4].count++;
      else bins[5].count++;
    });
    return bins;
  }, [nearestNeighborMap]);

  // ── Filter handlers ───────────────────────────────────────────────────

  const handleIslandChange = (e: SelectChangeEvent<string>) => {
    setIslandFilter(e.target.value);
    setRegionFilter('');
    setDistrictFilter('');
  };

  const handleRegionChange = (e: SelectChangeEvent<string>) => {
    setRegionFilter(e.target.value);
    setDistrictFilter('');
  };

  const handleDistrictChange = (e: SelectChangeEvent<string>) => {
    setDistrictFilter(e.target.value);
  };

  const handleTypeToggle = (type: string) => {
    setTypeFilters((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const handleLevelToggle = (level: string) => {
    setLevelFilters((prev) => ({ ...prev, [level]: !prev[level] }));
  };

  const handleResetFilters = () => {
    setIslandFilter('');
    setRegionFilter('');
    setDistrictFilter('');
    setTypeFilters({ PUBLIC: true, PRIVATE: true });
    setLevelFilters({ 'Pre-Primary': true, Primary: true, Secondary: true, Basic: true });
  };

  // ── Return value ──────────────────────────────────────────────────────

  return {
    // Filter state
    islandFilter,
    regionFilter,
    districtFilter,
    typeFilters,
    levelFilters,
    choroplethIndicator,
    setChoroplethIndicator,

    // Loading / error state
    isLoading: isLoadingSchools,
    isError: isErrorSchools,
    error: errorSchools,
    refetchSchools,

    // GeoJSON layers
    districtsGeoJSON,
    regionsGeoJSON,
    wardsGeoJSON,

    // Raw data
    allSchools,
    allRegions,

    // Filtered / derived data
    regions,
    districts,
    filteredSchools,
    gpsSchools,
    districtAggregates,
    stats,

    // Proximity & coastal analysis
    coastlinePolygon,
    coastalZoneGeoJSON,
    nearestNeighborMap,
    coastalDistanceMap,
    coastalStats,
    distanceDistribution,

    // Filter handlers
    handleIslandChange,
    handleRegionChange,
    handleDistrictChange,
    handleTypeToggle,
    handleLevelToggle,
    handleResetFilters,
  };
}

export default useSchoolData;
